import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PortfolioPerformanceBackendService } from '../../../pp/service/portfolio-performance-backend.service';
import { takeUntil } from 'rxjs/operators';
import { HorNavbarService } from '../../../../core/service/hor-navbar.service';
import { Subject } from 'rxjs';
import { OrganizationsService } from '../service/organizations.service';
import { CompanyBackendService } from 'src/app/domain/pp/service/company-backend.service';
import { ExcelService } from 'src/app/domain/excel';
import { ExcelTemplateName } from 'src/app/domain/excel/interfaces';
import { CompanyDto } from 'src/app/domain/recovery/component/recovery-company/dto/CompanyWithOrgDto';
import { ChangeRouteAndSwitchFund } from 'src/app/core/component/hornavbar/horizontal-nav-bar.component';
import { Organization } from '../../dto/Organization';
import { OrganizationPageService } from '../service/organization-page.service';
import { OrganizationOverviewDTO } from '../../dto/OrganizationOverviewDTO';
import { PortfolioPerformanceDTO } from 'src/app/domain/pp/dto/PortfolioPerformance';
import { FP_URLS_FULL_PATH } from "../../../fund-performance/component/fund-performances/pojo/FP_URLS";
import { CP_URLS } from '../../../company-performance/dto/CP_URLS';
import { ORG_URLS } from '../../dto/ORG_URLS';
import { PpUrlService } from '../../../pp/service/pp-url.service';
import { FpOverviewData, SelectedFundsAmount } from "../organization-overview/utils";
import {
  FundsSelectorDropdownService,
} from "../../../shared/component/funds-selector-dropdown/FundsSelectorDropdown.service";
import { FundPerformance } from '../../../fund-performance/dto/FundPerformance';

@Component({
  selector: 'app-org-pp-funds-overview',
  templateUrl: './org-pp-funds-overview.component.html',
  styleUrls: ['./org-pp-funds-overview.component.scss'],
})
export class OrgPpFundsOverviewComponent implements OnInit, OnChanges, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  @Input() selectedOrg: Organization;
  selectedPeriod: Date;
  selectedFPs: FundPerformance[];
  orgOverview: OrganizationOverviewDTO[];
  fundOverview: Overview[] = [];
  switcher = 1;
  total: OrganizationFundsTable;
  selectedCompany: CompanyDto;
  fundsData = new FpOverviewData();
  selectedFundsAmount = new SelectedFundsAmount();
  sortPpFund = SortPpFundOverview;

  constructor(
    private ppBackendService: PortfolioPerformanceBackendService,
    private navbarService: HorNavbarService,
    private orgService: OrganizationsService,
    private orgPageService: OrganizationPageService,
    private companyService: CompanyBackendService,
    private excelService: ExcelService,
    private ppUrlService: PpUrlService,
    private fundsSelectorDropdownService: FundsSelectorDropdownService,
  ) {
  }

  ngOnInit(): void {
    this.navbarService.getSelectedPeriod
      .pipe(takeUntil(this._destroyStream))
      .subscribe((period) => {
        if (period !== undefined) {
          this.selectedPeriod = period;
        }
      });

    this.navbarService.getNumberSwitcher
      .pipe(takeUntil(this._destroyStream))
      .subscribe((s) => {
        if (!s) {
          return;
        }
        this.switcher = s;
      });

    this.orgPageService.getFpOverview()
      .pipe(takeUntil(this._destroyStream))
      .subscribe((fps) => {
        if (!fps) {
          return;
        }
        this.selectedFPs = fps.map((f) => f.selectedFP);
        this.changePeriodAccordingToSelectedFP();
      });

    this.fundsSelectorDropdownService.fundsData.pipe(takeUntil(this._destroyStream)).subscribe((fundsData) => {
      if (!fundsData) {
        return;
      }
      this.fundsData = fundsData;
      this.fundsSelectorDropdownService.findSelected(this.selectedFundsAmount);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedOrg) {
      this.resetData();
      this.getPortfolios();
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }


  changePeriod(period: Date, overview: PortfolioPerformanceOverview): void {
    overview.selectedFundPeriod = period;
    overview.active.pps.forEach((i) => i.selectedPP = i.pps.find((pp) => pp.period === period)); // sets active selectedPP values
    const activePps = overview.active.pps.map((i) => i.selectedPP).filter((pp) => pp); // gets active selectedPP array
    overview.active.ppsLength = activePps.length;
    overview.sold.pps.forEach((i) => i.selectedPP = i.pps.find((pp) => pp.period === period)); // sets sold selectedPP values
    const soldPps = overview.sold.pps.map((i) => i.selectedPP).filter((pp) => pp); // gets sold selectedPP array
    overview.sold.ppsLength = soldPps.length;
    overview.active.total = this.countTotal(overview.active.pps.map((pp) => pp.selectedPP).filter((pp) => pp));
    overview.sold.total = this.countTotal(overview.sold.pps.map((pp) => pp.selectedPP).filter((pp) => pp));
    overview.total = this.countTotal([overview.active.total, overview.sold.total],
      overview.fundPerformances?.find((fp) => fp.period === period));
    overview.companiesAmount = [...new Set(activePps.concat(soldPps).map((i) => i.name))].length;
  }

  goToFund(o: Overview): void {
    const data: ChangeRouteAndSwitchFund = {
      fund: this.selectedOrg.funds.find((f) => f.uuid === o.fundUuid),
      orgUuid: this.selectedOrg.uuid,
      fp: o.overview.fundPerformances.find((fp) => fp.period === o.overview.selectedFundPeriod),
      route: FP_URLS_FULL_PATH.FUND,
    };
    this.orgService.setEmulateSwitchFund(data);
  }


  async goToCompanyPerformance(companyId: number, o: Overview): Promise<void> {
    this.selectedCompany = await this.companyService.getById(companyId);
    this.ppUrlService.setPrevURL(ORG_URLS.BASE + '/' + ORG_URLS.OVERVIEW);
    const data: ChangeRouteAndSwitchFund = {
      fund: this.selectedOrg.funds.find((f) => f.uuid === o.fundUuid),
      orgUuid: this.selectedOrg.uuid,
      fp: o.overview.fundPerformances.find((fp) => fp.period === o.overview.selectedFundPeriod),
      route: `${CP_URLS.BASE}/${this.selectedCompany.uuid}`,
    };
    this.orgService.setEmulateSwitchFund(data);
  }

  exportExcel(): void {
    const data = this.fundOverview.filter((fund) => fund.isShown);
    const fileName = this.selectedOrg.name + '-portfolio-performance-by-funds.xlsx';
    this.excelService.exportByTemplate(data, fileName, ExcelTemplateName.ppByFundTemplate);
  }

  private resetData(): void {
    this.total = undefined;
    this.selectedFPs = undefined;
    this.selectedPeriod = undefined;
    this.orgOverview = undefined;
    this.fundOverview = undefined;
  }

  private async getPortfolios(): Promise<void> {
    this.orgOverview = await this.ppBackendService.getOverview(this.selectedOrg.uuid);
    const fundOverview: Overview[] = [];
    for (const fundPPs of this.orgOverview) {
      // eslint-disable-next-line no-nested-ternary
      const fundIdFromPP = fundPPs.active.length ? fundPPs.active[0].fundId : (fundPPs.sold.length ? fundPPs.sold[0].fundId : null);

      if (fundIdFromPP === null) {
        // fund could not be parsed =/
        continue;
      }

      const fund = this.selectedOrg.funds.find((f) => f.id === fundIdFromPP);
      const uniquePeriodsForFund = new Set(fundPPs.active.map((pp) => pp.period).concat(fundPPs.sold.map((pp) => pp.period)));

      const toBeAddedCompanies: {
        active: PortfolioPerformanceByCompany[];
        sold: PortfolioPerformanceByCompany[];
      } = { active: [], sold: [] };

      let allCompanyNames: string[] = [];
      for (const isRealizedType of ['active', 'sold']) {
        // Parses unique company names from Portfolio Performances.
        const companyNames = [...new Set((fundPPs[isRealizedType] as PortfolioPerformanceDTO[]).map((pp) => pp.name))];
        allCompanyNames = [...allCompanyNames, ...companyNames];
        for (const companyName of companyNames) {
          const companyPPs: PortfolioPerformanceDTO[] = (fundPPs[isRealizedType] as PortfolioPerformanceDTO[]).filter((pp) => pp.name === companyName);
          (toBeAddedCompanies[isRealizedType] as PortfolioPerformanceByCompany[]).push({
            company: {
              name: companyName,
              id: companyPPs[0].companyId,
            },
            pps: this.ppDtoListToEntity(companyPPs),
            selectedPP: null,
          });
        }
      }

      const companiesAmount = [...new Set(allCompanyNames)].length;

      fundOverview.push({
        fundName: fund.name,
        fundUuid: fund.uuid,
        isShown: true,
        isOpened: false,
        overview: {
          companiesAmount,
          active: {
            pps: toBeAddedCompanies.active,
            ppsLength: toBeAddedCompanies.active.length,
            isOpened: false,
            total: null,
          },
          sold: {
            pps: toBeAddedCompanies.sold,
            ppsLength: toBeAddedCompanies.sold.length,
            isOpened: false,
            total: null,
          },
          uniqueFundPeriods: [...uniquePeriodsForFund],
          selectedFundPeriod: uniquePeriodsForFund.values().next().value as Date,
          fundPerformances: fundPPs.fundPerformances,
          total: null,
        },
      } as unknown as Overview);
    }
    fundOverview.sort((a, b) => a.fundName.localeCompare(b.fundName));
    this.fundOverview = fundOverview;
    if (!this.selectedFPs) {
      for (const fund of this.fundOverview) {
        this.changePeriod(fund.overview.selectedFundPeriod, fund.overview);
      }
    } else {
      this.changePeriodAccordingToSelectedFP();
    }
    this.calculateGrandTotal();
  }

  private calculateGrandTotal(): void {
    if (!this.fundOverview || !this.fundOverview.length) {
      return;
    }
    const investment = this.fundOverview.map((fo) =>
      (fo.overview.total?.investment && fo.isShown ? fo.overview.total.investment : 0)).reduce((a, b) => a + b, 0);

    const fairValue = this.fundOverview.map((fo) =>
      (fo.overview.total?.fairValue && fo.isShown ? fo.overview.total.fairValue : 0)).reduce((a, b) => a + b, 0);
    this.total = {
      name: 'Total',
      fairValue,
      moic: fairValue ? fairValue / investment : null,
      companyValue: null,
      holding: null,
      investment,
      period: null,
      irr: null,
    };

  }

  private changePeriodAccordingToSelectedFP(): void {
    const fundsAreShown: Overview[] = [];
    this.selectedFPs
      .filter((fp) => fp)
      .forEach((fp) => {
        const fund = this.fundOverview?.find((f) =>
          f.overview.fundPerformances?.find((fp_) => fp_.id === fp.id));
        if (!fund) {
          return;
        }
        fundsAreShown.push(fund);
        this.changePeriod(fp.period, fund.overview);
      });

    this.fundOverview?.forEach((fo) => {
      fo.isShown = Boolean(fundsAreShown.find((fas) => fas.fundName === fo.fundName));
    });
    this.calculateGrandTotal();
  }

  /**
   * Counts totals for given fp.
   *
   * @param pps are portfolio performances of companies of fund.
   * @param fp is a fund performance. Contains IRR for the table.
   * @return calculated total.
   */
  private countTotal(pps: OrganizationFundsTable[], fp?: FundPerformance): OrganizationFundsTable {
    const name = pps?.length ? pps[0].name : null;
    const companyValue = pps.reduce((a, b) => a + b?.companyValue, 0);
    const holding = null;
    const irr = fp ? fp.calculatedGrossIrr : null;
    const investment = pps.reduce((a, b) => a + Math.round(b?.investment), 0);
    const fairValue = pps.reduce((a, b) => a + Math.round(b?.fairValue), 0);
    const moic = investment ? fairValue / investment : null;
    const period = null;
    return {
      name, companyValue, holding, irr, investment, fairValue, moic, period,
    };
  }

  private ppDtoListToEntity(pps: PortfolioPerformanceDTO[]): OrganizationFundsTable[] {
    return pps.map((pp) => this.ppDtoToEntity(pp));
  }

  private ppDtoToEntity(pp: PortfolioPerformanceDTO): OrganizationFundsTable {
    const name = pp.name;
    const investment = pp.cost;
    const fairValue = pp.fairValue;
    const moic = investment ? fairValue / investment : null;
    const irr = pp.calculatedGrossIrr;
    const holding = pp.holdingPercentage;
    const companyValue = pp.currentCompanyValuation;
    const period = pp.period;
    return { name, investment, fairValue, moic, irr, holding, companyValue, period };
  }
}

export interface Overview {
  fundName: string;
  fundUuid: string;
  companiesAmount: number;
  isOpened: boolean;
  isShown: boolean;
  overview: PortfolioPerformanceOverview;
}

class PortfolioPerformanceOverview {
  public companiesAmount: number;
  public active: IsRealizedType;
  public sold: IsRealizedType;
  public selectedFundPeriod: Date;
  public fundPerformances: FundPerformance[];
  public total: OrganizationFundsTable;
}

interface IsRealizedType {
  pps: PortfolioPerformanceByCompany[];
  isOpened: boolean;
  ppsLength: number;
  total: OrganizationFundsTable;
}

export interface OrganizationFundsTable {
  name: string;
  period: Date;
  investment: number;
  fairValue: number;
  moic: number;
  irr: number;
  holding: number;
  companyValue: number;
}

export class PortfolioPerformanceByCompany {
  public company: {
    name: string;
    id: number;
  };

  public pps: OrganizationFundsTable[];
  public selectedPP: OrganizationFundsTable;
}

export const SortPpFundOverview = {
  byFundName: (overview: Overview): string => overview.fundName,
  byPeriod: (overview: Overview): Date => overview.overview.selectedFundPeriod,
  byInvestment: (overview: Overview): number => overview.overview.total?.investment,
  byFairValue: (overview: Overview): number => overview.overview.total?.fairValue,
  byGainLoss: (overview: Overview): number => overview.overview.total?.fairValue - overview.overview.total?.investment,
  byMoic: (overview: Overview): number => overview.overview.total?.moic,
  byIrr: (overview: Overview): number => overview.overview.total?.irr,
  byHolding: (overview: Overview): number => overview.overview.total?.holding,
};
